import { NoTransition, ToastContainer, Zoom } from '@dvb/common';
import { useSettingStore } from '@milestone-portal/context/useSettingStore';
import { router } from '@milestone-portal/router';
import { Logger } from '@milestone-portal/utils/logger';
import { toast } from '@milestone-portal/utils/toast';
import * as Sentry from '@sentry/react';
import { onlineManager, type QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import { lazy, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { RootError } from './-components/error/RootError';

const MIN_STORAGE_SIZE = 1024 * 1024 * 10; // 10MB

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: RootPage,
  errorComponent: RootError,
});

const TanStackRouterDevtools =
  import.meta.env.VITE_APP_IS_ENABLE_TANSTACK_DEVTOOLS === 'true'
    ? lazy(() =>
        import('@tanstack/router-devtools').then((d) => ({
          default: d.TanStackRouterDevtools,
        }))
      )
    : () => null;

const TanStackQueryDevtools =
  import.meta.env.VITE_APP_IS_ENABLE_TANSTACK_DEVTOOLS === 'true'
    ? lazy(() =>
        import('@tanstack/react-query-devtools').then((d) => ({
          default: d.ReactQueryDevtools,
        }))
      )
    : () => null;

/**
 * Since both onlineManager and navigator.onLine are not reliable enough
 * - For onlineManager, it will be false positive when entry the page offline at the very first.
 * - For navigator.onLine, it will be false positive when only able to access LAN or router.
 * we need to check the online status though a network request.
 *
 * @see https://jfhr.me/how-to-really-know-if-your-webapp-is-online
 */
const syncOnlineStatus = async () => {
  if (!navigator.onLine) {
    onlineManager.setOnline(false);
    return;
  }

  const headers = new Headers();
  headers.append('cache-control', 'no-cache');
  headers.append('pragma', 'no-cache');

  try {
    await fetch(window.location.origin, { headers, method: 'HEAD' });
    onlineManager.setOnline(true);
  } catch (error) {
    if (error instanceof TypeError) {
      onlineManager.setOnline(false);
    }
    throw error;
  }
};

function RootPage() {
  const { i18n, t } = useTranslation();
  const { hasAnimation, isDarkMode } = useSettingStore();

  const initSentry = async () => {
    const {
      DEV: isDevelopment,
      VITE_APP_SENTRY_DSN,
      VITE_APP_SENTRY_ENV,
    } = import.meta.env;

    if (
      !VITE_APP_SENTRY_DSN ||
      !VITE_APP_SENTRY_ENV ||
      Sentry.getClient() != null
    )
      return;

    try {
      Sentry.init({
        beforeSend: (event) => {
          // Filter axios error
          if (event.exception?.values?.[0]?.type === 'AxiosError') {
            return null;
          }
          return event;
        },

        dsn: VITE_APP_SENTRY_DSN,
        environment: VITE_APP_SENTRY_ENV,
        integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
        profilesSampleRate: 1.0, // Based on traces (tracesSampleRate * profilesSampleRate)
        release: APP_VERSION,
        replaysOnErrorSampleRate: 1.0,
        replaysSessionSampleRate: isDevelopment ? 1.0 : 0.25,
        tracesSampleRate: isDevelopment ? 1.0 : 0.25,
        transport: Sentry.makeBrowserOfflineTransport(
          Sentry.makeFetchTransport
        ),
      });
    } catch (error: any) {
      Logger.error(error?.message || error);
    }
  };

  const initDayjs = () => {
    dayjs.locale(i18n.language.toLowerCase());
  };

  const checkStorage = async () => {
    // IOS < 17 not support estimate
    if (!navigator?.storage?.estimate) return;
    const storage = await navigator.storage.estimate();
    if (storage.quota && storage.quota <= MIN_STORAGE_SIZE) {
      toast.error(t('notEnoughStorageDetected', { ns: 'ui' }), {
        autoClose: false,
      });
    }
  };

  useEffect(() => {
    initSentry();
    initDayjs();
    checkStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    syncOnlineStatus();
  }, []);

  return (
    <>
      <div className="bg-background flex min-h-screen transition-[background-color] duration-300">
        <Outlet />
      </div>
      <ToastContainer
        autoClose={4000}
        className="mt-[env(safe-area-inset-top)]"
        closeButton={false}
        closeOnClick={false}
        draggablePercent={20}
        limit={3}
        pauseOnFocusLoss={false}
        pauseOnHover={false}
        position="top-center"
        theme={isDarkMode ? 'dark' : 'light'}
        transition={hasAnimation ? Zoom : NoTransition}
      />
      <Suspense>
        <TanStackRouterDevtools />
        <TanStackQueryDevtools />
      </Suspense>
    </>
  );
}
